import React, { createContext, useContext, useMemo, useState } from 'react';
import { getItemSync } from '../utils/storage';

const SOUND_ENABLED_KEY = 'soundEnabled';
const SOUND_VOLUME_KEY = 'soundVolume';
const DEFAULT_SOUND_ENABLED = true;
const DEFAULT_SOUND_VOLUME = 80;

const SoundSettingsContext = createContext(null);

const clampVolume = (value) => {
  if (Number.isNaN(value)) return DEFAULT_SOUND_VOLUME;
  return Math.max(0, Math.min(100, Math.round(value)));
};

export const SoundSettingsProvider = ({ children }) => {
  const [enabled, setEnabledState] = useState(() => {
    const stored = getItemSync(SOUND_ENABLED_KEY);
    return stored == null ? DEFAULT_SOUND_ENABLED : stored !== 'false';
  });
  const [volume, setVolumeState] = useState(() => {
    const stored = getItemSync(SOUND_VOLUME_KEY);
    if (stored == null) return DEFAULT_SOUND_VOLUME;
    return clampVolume(parseInt(stored, 10));
  });

  const setEnabled = (value) => {
    setEnabledState(value);
    try {
      localStorage.setItem(SOUND_ENABLED_KEY, String(value));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[sound] failed to persist soundEnabled:', error);
      }
    }
  };

  const setVolume = (value) => {
    const next = clampVolume(value);
    setVolumeState(next);
    try {
      localStorage.setItem(SOUND_VOLUME_KEY, String(next));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[sound] failed to persist soundVolume:', error);
      }
    }
  };

  const contextValue = useMemo(() => ({
    enabled,
    setEnabled,
    volume,
    setVolume,
  }), [enabled, volume]);

  return (
    <SoundSettingsContext.Provider value={contextValue}>
      {children}
    </SoundSettingsContext.Provider>
  );
};

export const useSoundSettings = () => {
  const context = useContext(SoundSettingsContext);
  if (!context) {
    throw new Error('useSoundSettings must be used within SoundSettingsProvider');
  }
  return context;
};

export default SoundSettingsContext;
